import { useCallback, useState } from "react";

const EMPTY_SCOPES = JSON.stringify({ scopes: [] }, null, 2);

const validateScopes = (content) => {
  try {
    const parsed = JSON.parse(content);
    if (!Array.isArray(parsed.scopes)) return "Scopes JSON requires a scopes array";
    if (parsed.scopes.some((scope) => typeof scope !== "string" || !scope.trim())) {
      return "Scopes must contain non-empty strings";
    }
    return null;
  } catch (error) {
    return `Invalid JSON: ${error.message}`;
  }
};

const useProviderDraft = () => {
  const [initialScopesContent, setInitialScopesContent] = useState("");
  const [scopesDraft, setScopesDraft] = useState("");
  const [scopesBuffer, setScopesBuffer] = useState("");
  const [scopesBufferBaseline, setScopesBufferBaseline] = useState("");
  const [isEditingScopes, setIsEditingScopes] = useState(false);
  const [scopesError, setScopesError] = useState(null);
  const [initialIconPath, setInitialIconPath] = useState("");
  const [iconFile, setIconFile] = useState(null);
  const [iconAction, setIconAction] = useState("retain");

  // Stable identity so callers can safely list it as an effect dependency.
  const resetDraft = useCallback(({ scopesContent = "", iconPath = "" } = {}) => {
    setInitialScopesContent(scopesContent);
    setScopesDraft(scopesContent);
    setScopesBuffer("");
    setScopesBufferBaseline("");
    setIsEditingScopes(false);
    setScopesError(null);
    setInitialIconPath(iconPath);
    setIconFile(null);
    setIconAction("retain");
  }, []);

  const openScopesEditor = () => {
    const content = scopesDraft || EMPTY_SCOPES;
    setScopesBuffer(content);
    setScopesBufferBaseline(scopesDraft);
    setScopesError(validateScopes(content));
    setIsEditingScopes(true);
  };

  const updateScopesBuffer = (content) => {
    setScopesBuffer(content);
    setScopesError(validateScopes(content));
  };

  const applyScopes = () => {
    const error = validateScopes(scopesBuffer);
    setScopesError(error);
    if (error) return false;
    setScopesDraft(scopesBuffer);
    setIsEditingScopes(false);
    return true;
  };

  const discardScopes = () => {
    setScopesBuffer("");
    setScopesBufferBaseline("");
    setScopesError(null);
    setIsEditingScopes(false);
  };

  const selectScopesFile = async (file) => {
    if (!file) {
      setScopesDraft("");
      return false;
    }
    const content = await file.text();
    const error = validateScopes(content);
    setScopesError(error);
    if (!error) setScopesDraft(JSON.stringify(JSON.parse(content), null, 2));
    return !error;
  };

  const selectIcon = (file) => {
    setIconFile(file);
    setIconAction(file ? "replace" : initialIconPath ? "remove" : "retain");
  };

  const scopesEditorDirty = isEditingScopes && scopesBuffer !== scopesBufferBaseline;
  const isDraftDirty = scopesDraft !== initialScopesContent || iconAction !== "retain";

  return {
    scopesDraft,
    scopesBuffer,
    scopesBufferBaseline,
    scopesError,
    isEditingScopes,
    scopesEditorDirty,
    hasPendingEditorTransaction: scopesEditorDirty,
    isDraftDirty,
    initialIconPath,
    iconFile,
    iconAction,
    resetDraft,
    openScopesEditor,
    updateScopesBuffer,
    applyScopes,
    discardScopes,
    selectScopesFile,
    selectIcon,
  };
};

export default useProviderDraft;